import { startAuthentication } from '@simplewebauthn/browser';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { AuthApi, Me, OrgMembership, PlanNotice, SignInResult, User } from '../api/AuthApi.ts';
import { ApiError } from '../http/ApiError.ts';

export type SessionStatus = 'unknown' | 'authenticated' | 'anonymous';

/**
 * The signed-in user. `ensureLoaded()` asks the server once per page load;
 * after that, login/logout keep it current. The server cookie is the source of
 * truth; this store never persists anything to browser storage.
 */
export const useSessionStore = defineStore('session', () => {
  const user = ref<User | null>(null);
  const orgs = ref<OrgMembership[]>([]);
  /** Whether two-factor is on (organizations may require it). */
  const twoFactor = ref(false);
  /** Over your plan's people (the banner), or null. */
  const planNotice = ref<PlanNotice | null>(null);
  const status = ref<SessionStatus>('unknown');
  let loading: Promise<void> | null = null;
  let api: AuthApi | null = null;

  const isAuthenticated = computed(() => status.value === 'authenticated');

  function bind(authApi: AuthApi): void {
    api = authApi;
  }

  function client(): AuthApi {
    if (!api) throw new Error('Session store used before bind(authApi)');
    return api;
  }

  function apply(me: Me | null): void {
    user.value = me?.user ?? null;
    orgs.value = me?.orgs ?? [];
    twoFactor.value = me?.twoFactor === true;
    planNotice.value = me ? me.planNotice : null;
    status.value = me ? 'authenticated' : 'anonymous';
  }

  async function refresh(): Promise<void> {
    try {
      apply(await client().me());
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) apply(null);
      else throw err;
    }
  }

  function ensureLoaded(): Promise<void> {
    if (status.value !== 'unknown') return Promise.resolve();
    loading ??= refresh().finally(() => {
      loading = null;
    });
    return loading;
  }

  /** Signed in (null), or the challenge for the second step (two-factor on). */
  async function login(email: string, password: string): Promise<string | null> {
    return finish(await client().login({ email, password }));
  }

  /** The second step: signs in when the code (or recovery code) is right. */
  async function completeSecondFactor(
    challenge: string,
    factor: { code: string } | { recoveryCode: string },
  ): Promise<void> {
    await client().completeSecondFactor({ challenge, ...factor });
    await refresh();
  }

  async function finish(result: SignInResult): Promise<string | null> {
    if ('secondFactor' in result) return result.secondFactor.challenge;
    await refresh();
    return null;
  }

  /** Sign in with a passkey: the browser asks which one, and for the device's unlock. */
  async function signInWithPasskey(): Promise<void> {
    const { options, ticket } = await client().passkeySignInOptions();
    const response = await startAuthentication({ optionsJSON: options });
    await client().signInWithPasskey({ ticket, response });
    await refresh();
  }

  async function verifyEmail(token: string): Promise<void> {
    await client().verifyEmail(token);
    await refresh();
  }

  /** Signed in (null), or the challenge for the second step (two-factor on). */
  async function resetPassword(token: string, password: string): Promise<string | null> {
    return finish(await client().resetPassword({ token, password }));
  }

  async function logout({
    everywhere = false,
    pushDevice = null,
  }: { everywhere?: boolean; pushDevice?: string | null } = {}): Promise<void> {
    try {
      await (everywhere ? client().logoutEverywhere() : client().logout(pushDevice));
    } finally {
      apply(null);
    }
  }

  /** The server ended our session (e.g. a 401 mid-use): forget the user locally. */
  function expire(): void {
    apply(null);
  }

  return {
    user,
    orgs,
    twoFactor,
    planNotice,
    status,
    isAuthenticated,
    bind,
    ensureLoaded,
    refresh,
    login,
    completeSecondFactor,
    signInWithPasskey,
    verifyEmail,
    resetPassword,
    logout,
    expire,
  };
});
